export interface TimerOptions {
  onTick?: (remaining: number) => void;
  onEnd?: () => void;
}

export interface Timer {
  start(): void;
  stop(): void;
  pause(): void;
  resume(): void;
  adjust(deltaSeconds: number): void;
  getRemaining(): number;
}

export function createTimer(seconds: number, { onTick, onEnd }: TimerOptions = {}): Timer {
  let remaining = seconds;
  let intervalId: number | null = null;
  let ended = false;

  function clear(): void {
    if (intervalId !== null) {
      clearInterval(intervalId);
      intervalId = null;
    }
  }

  function finish(): void {
    if (ended) return;
    ended = true;
    clear();
    onEnd?.();
  }

  function tick(): void {
    remaining -= 1;
    if (remaining <= 0) {
      remaining = 0;
      onTick?.(remaining);
      finish();
      return;
    }
    onTick?.(remaining);
  }

  function start(): void {
    if (intervalId !== null || ended) return;
    onTick?.(remaining);
    intervalId = window.setInterval(tick, 1000);
  }

  function pause(): void {
    clear();
  }

  function resume(): void {
    if (intervalId !== null || ended || remaining <= 0) return;
    intervalId = window.setInterval(tick, 1000);
  }

  function stop(): void {
    clear();
    ended = true;
  }

  function adjust(deltaSeconds: number): void {
    if (ended) return;
    remaining = Math.max(0, remaining + deltaSeconds);
    onTick?.(remaining);
    if (remaining <= 0) finish();
  }

  function getRemaining(): number {
    return remaining;
  }

  return { start, stop, pause, resume, adjust, getRemaining };
}
